import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  TrendingUp, DollarSign, FolderOpen, Activity, 
  Zap, Pickaxe, Droplet, Building2, Package, Wheat, Home,
  Target, Shield, FileText, Clock, Globe, Users, Gem, AlertTriangle
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTOR_ICONS = {
  'Energy / Oil & Gas': Zap, 'Minerals / Metals': Pickaxe, 'Other Industrial': Building2,
};

const COLORS = ['#FF4F00', '#475569', '#10B981', '#F59E0B', '#06B6D4', '#8B5CF6', '#EC4899', '#14B8A6'];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-sm p-2 shadow-lg text-xs">
        <p className="font-semibold">{payload[0].name || payload[0].payload?.label}</p>
        <p className="text-muted-foreground">{payload[0].value} deals</p>
      </div>
    );
  }
  return null;
};

const TickerStrip = () => {
  const [items, setItems] = useState([]);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchTicker = async () => {
      try {
        const res = await axios.get(`${API}/markets/live-ticker`, { headers: getAuthHeaders() });
        if (res.data && res.data.length > 0) {
          setItems(res.data);
        }
      } catch {
        // fallback static
        setItems([
          { label: 'Gold', value: '$2,042/oz', change: '+0.8%', up: true },
          { label: 'Copper', value: '$8,485/t', change: '+1.2%', up: true },
          { label: 'Brent Crude', value: '$82.4/bbl', change: '+0.9%', up: true },
        ]);
      }
    };
    fetchTicker();
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="overflow-hidden border-y border-border bg-muted/30" data-testid="market-ticker">
      <div className="flex animate-scroll whitespace-nowrap py-2">
        {[...items, ...items].map((item, i) => (
          <span key={i} className="inline-flex items-center gap-2 px-6 text-xs">
            <span className="font-medium">{item.label}</span>
            <span className="font-mono">{item.value}</span>
            <span className={`font-mono ${item.up ? 'text-green-500' : 'text-red-500'}`}>{item.change}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export function DashboardPage() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [activities, setActivities] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [res, actRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API}/activities/personalized`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      setAnalytics(res.data);
      setActivities(actRes.data);

      // Fetch user's own submissions (deals + minerals)
      if (user?.role && !['admin', 'internal'].includes(user.role)) {
        const subRes = await axios.get(`${API}/users/me/submissions`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }));
        setMySubmissions(subRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading dashboard...</div></div>;

  const countryCount = analytics?.countries?.length || 0;
  const sectorBreakdown = analytics?.sectors?.sort((a, b) => b.total_capital - a.total_capital).slice(0, 5) || [];

  return (
    <div className="min-w-0 overflow-hidden" data-testid="dashboard-page">
      <TickerStrip />
      
      <div className="p-6 space-y-6 min-w-0">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back, {user?.full_name}</p>
        </div>

        {/* KPI Cards - all clickable */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/deals')} data-testid="stat-total-deals">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-1">
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
                {analytics?.pending_deals > 0 && <Badge variant="outline" className="text-[9px] px-1 py-0 border-yellow-400 text-yellow-600">{analytics.pending_deals} pending</Badge>}
              </div>
              <div className="text-2xl font-bold font-mono">{analytics?.total_listings || 0}</div>
              <p className="text-[10px] text-muted-foreground">Total Listings</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/pipeline')} data-testid="stat-total-capital">
            <CardContent className="pt-4 pb-3">
              <DollarSign className="h-4 w-4 text-muted-foreground mb-1" />
              <div className="text-2xl font-bold font-mono">{formatCurrency(analytics?.total_capital_required || 0)}</div>
              <p className="text-[10px] text-muted-foreground">Capital Required</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/matching')} data-testid="stat-capital-matched">
            <CardContent className="pt-4 pb-3">
              <Target className="h-4 w-4 text-muted-foreground mb-1" />
              <div className="text-2xl font-bold font-mono">{formatCurrency(analytics?.total_capital_matched || 0)}</div>
              <p className="text-[10px] text-muted-foreground">Capital Matched</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/minerals')} data-testid="stat-minerals">
            <CardContent className="pt-4 pb-3">
              <Gem className="h-4 w-4 text-muted-foreground mb-1" />
              <div className="text-2xl font-bold font-mono">{analytics?.total_minerals || 0}</div>
              <p className="text-[10px] text-muted-foreground">Mineral Listings</p>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/crm')} data-testid="stat-users">
            <CardContent className="pt-4 pb-3">
              <Users className="h-4 w-4 text-muted-foreground mb-1" />
              <div className="text-2xl font-bold font-mono">{analytics?.total_users || 0}</div>
              <p className="text-[10px] text-muted-foreground">Participants</p>
            </CardContent>
          </Card>

          {user?.role && ['admin', 'internal'].includes(user.role) && (
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/commissions')} data-testid="stat-commissions">
            <CardContent className="pt-4 pb-3">
              <TrendingUp className="h-4 w-4 text-muted-foreground mb-1" />
              <div className="text-2xl font-bold font-mono">{formatCurrency(analytics?.total_commissions || 0)}</div>
              <p className="text-[10px] text-muted-foreground">Commissions</p>
            </CardContent>
          </Card>
          )}
        </div>

        {/* Pipeline Value Tracker */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/pipeline')} data-testid="pipeline-tracker">
          <CardContent className="pt-5 pb-4 min-w-0">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Deal Pipeline Value</p>
                <div className="text-4xl font-bold font-mono tracking-tight">{formatCurrency(analytics?.total_capital_required || 0)}</div>
              </div>
              <div className="flex gap-4 sm:gap-8 flex-wrap">
                <div className="text-right">
                  <p className="text-2xl font-bold font-mono">{analytics?.total_deals || 0}</p>
                  <p className="text-xs text-muted-foreground">Deals</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold font-mono">{countryCount}</p>
                  <p className="text-xs text-muted-foreground">Countries</p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {sectorBreakdown.map((s, i) => (
                <Badge key={i} variant="outline" className="text-[10px]">{s._id}: {formatCurrency(s.total_capital)}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="chart-sectors">
            <CardHeader><CardTitle>Deals by Sector</CardTitle></CardHeader>
            <CardContent>
              {analytics?.sectors && analytics.sectors.length > 0 ? (
                <div className="flex items-start gap-6 min-w-0">
                  <div className="w-[180px] h-[180px] flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={analytics.sectors} dataKey="count" nameKey="_id" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                          {analytics.sectors.map((_, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 flex flex-col space-y-2 pt-1">
                    {analytics.sectors.sort((a, b) => a._id.localeCompare(b._id)).map((entry, index) => {
                      const Icon = SECTOR_ICONS[entry._id] || FolderOpen;
                      return (
                        <div key={`legend-${index}`} className="flex items-center space-x-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                          <Icon size={12} className="text-muted-foreground flex-shrink-0" />
                          <span className="text-xs text-muted-foreground truncate flex-1">{entry._id}</span>
                          <span className="text-xs font-mono font-semibold">{entry.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : <div className="h-[180px] flex items-center justify-center text-muted-foreground">No data</div>}
            </CardContent>
          </Card>

          <Card data-testid="chart-stages">
            <CardHeader><CardTitle>Deals by Stage</CardTitle></CardHeader>
            <CardContent>
              {analytics?.stages && analytics.stages.length > 0 ? (() => {
                const ORDER = ['concept', 'feasibility', 'development', 'financial_close', 'construction', 'operational'];
                const SCOLORS = { concept: '#9ca3af', feasibility: '#3b82f6', development: '#eab308', financial_close: '#a855f7', construction: '#f97316', operational: '#22c55e' };
                const LABELS = { concept: 'Concept', feasibility: 'Feasibility', development: 'Development', financial_close: 'Fin. Close', construction: 'Construction', operational: 'Operational' };
                const data = ORDER.map(s => ({ stage: s, label: LABELS[s], count: analytics.stages.find(x => x._id === s)?.count || 0, fill: SCOLORS[s] }));
                return (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '11px' }} formatter={(v) => [v, 'Deals']} />
                      <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                        {data.map((e, i) => <Cell key={`bar-${i}`} fill={e.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })() : <div className="h-[180px] flex items-center justify-center text-muted-foreground">No data</div>}
            </CardContent>
          </Card>
        </div>

        {/* Activity Feed + Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2" data-testid="activity-feed">
            <CardHeader><CardTitle className="flex items-center gap-2"><Activity size={18} />Activity Feed</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {activities.length > 0 ? activities.slice(0, 10).map((act, i) => (
                  <div key={`act-${act.id || i}`} className="flex items-center gap-3 p-2 rounded border border-border hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => act.entity_id && navigate(`/deals/${act.entity_id}`)} data-testid={`feed-activity-${i}`}>
                    <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${
                      act.activity_type === 'interest_expressed' ? 'bg-pink-100 dark:bg-pink-900/30' :
                      act.activity_type === 'info_requested' ? 'bg-purple-100 dark:bg-purple-900/30' :
                      act.activity_type === 'match_sent' ? 'bg-blue-100 dark:bg-blue-900/30' :
                      'bg-orange-100 dark:bg-orange-900/30'
                    }`}>
                      {act.activity_type === 'interest_expressed' ? <Target size={12} className="text-pink-600" /> :
                       act.activity_type === 'info_requested' ? <FileText size={12} className="text-purple-600" /> :
                       act.activity_type === 'match_sent' ? <Target size={12} className="text-blue-600" /> :
                       <FileText size={12} className="text-orange-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{act.description}</p>
                      <p className="text-[10px] text-muted-foreground">{act.activity_type?.replace(/_/g, ' ')} &middot; {act.entity_type}</p>
                    </div>
                  </div>
                )) : (
                  <>
                    {analytics?.recent_deals?.map((deal) => (
                      <div key={`deal-${deal.id}`} className="flex items-center gap-3 p-2 rounded border border-border hover:bg-muted/50 transition-colors cursor-pointer" onClick={() => navigate(`/deals/${deal.id}`)} data-testid={`feed-deal-${deal.id}`}>
                        <div className="w-7 h-7 rounded bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center flex-shrink-0"><FileText size={12} className="text-orange-600" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{deal.name}</p>
                          <p className="text-[10px] text-muted-foreground">{deal.sector} &middot; {deal.country}</p>
                        </div>
                        <Badge variant="outline" className="capitalize text-[9px] flex-shrink-0">{deal.status?.replace('_', ' ') || 'active'}</Badge>
                      </div>
                    ))}
                    {(!analytics?.recent_deals?.length) && (
                      <div className="text-center py-6 text-muted-foreground text-xs">No recent activity</div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="quick-stats">
            <CardHeader><CardTitle className="flex items-center gap-2"><Clock size={18} />Quick Overview</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="p-2.5 rounded bg-muted/50 cursor-pointer hover:bg-muted transition-colors" onClick={() => navigate('/deals')}>
                <p className="text-[10px] text-muted-foreground">Active Deals</p>
                <p className="text-lg font-bold font-mono">{analytics?.active_deals || 0}</p>
              </div>
              <div className="p-2.5 rounded bg-muted/50 cursor-pointer hover:bg-muted transition-colors" onClick={() => navigate('/matching')}>
                <p className="text-[10px] text-muted-foreground">Capital Matched</p>
                <p className="text-lg font-bold font-mono">{formatCurrency(analytics?.total_capital_matched || 0)}</p>
              </div>
              <div className="p-2.5 rounded bg-muted/50 cursor-pointer hover:bg-muted transition-colors" onClick={() => navigate('/access-requests')}>
                <p className="text-[10px] text-muted-foreground">Pending Requests</p>
                <p className="text-lg font-bold font-mono">{analytics?.recent_requests?.filter(r => r.status === 'pending').length || 0}</p>
              </div>
              <div className="p-2.5 rounded bg-muted/50 cursor-pointer hover:bg-muted transition-colors" onClick={() => navigate('/crm')}>
                <p className="text-[10px] text-muted-foreground">Participant Network</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {analytics?.user_roles?.sort((a,b) => a._id.localeCompare(b._id)).map((r, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] capitalize">{r._id}: {r.count}</Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* My Submissions section - shown for non-admin participants */}
        {user?.role && !['admin', 'internal'].includes(user.role) && mySubmissions.length > 0 && (
          <Card data-testid="my-submissions">
            <CardHeader><CardTitle className="flex items-center gap-2"><FolderOpen size={18} />My Submissions</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {mySubmissions.map((item) => {
                  const statusMap = {
                    pending_approval: { label: 'Pending Approval', color: 'border-yellow-400 text-yellow-600' },
                    approved: { label: 'Approved', color: 'border-green-400 text-green-600' },
                    active: { label: 'Approved', color: 'border-green-400 text-green-600' },
                    available: { label: 'Available', color: 'border-green-400 text-green-600' },
                    rejected: { label: 'Rejected', color: 'border-red-400 text-red-600' },
                    under_review: { label: 'Under Review', color: 'border-blue-400 text-blue-600' },
                    negotiating: { label: 'Negotiating', color: 'border-purple-400 text-purple-600' },
                  };
                  const st = statusMap[item.status] || { label: item.status?.replace(/_/g, ' '), color: '' };
                  const route = item.type === 'mineral' ? `/minerals/${item.id}` : `/deals/${item.id}`;
                  return (
                    <div key={item.id} className="flex items-center justify-between p-3 border border-border rounded-sm hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate(route)} data-testid={`submission-${item.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <Badge variant="outline" className="text-[9px] capitalize">{item.type}</Badge>
                        </div>
                        <p className="text-[10px] text-muted-foreground">{item.sector} &middot; {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</p>
                      </div>
                      <Badge variant="outline" className={`text-[10px] capitalize ${st.color}`}>{st.label}</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
